import i18next from "i18next";

import {
  ANALYST,
  ANALYST_ADMIN,
  BUSINESS_MANAGER,
  CONTENT_MANAGER,
  CONTENT_MANAGER_ADMIN,
  EDITOR_ADMIN,
  EDITORIAL_MANAGER,
  FAST_ADMIN,
  FAST_ANALYST,
  MODERATOR_ADMIN,
  OWNER,
  PROJECT_ADMIN,
} from "../constants/roles";

import type { TFunction } from "i18next";

import type { RoleByScope } from "../api/AdminUserAPI.types";

class AdminRoleByScope {
  t: TFunction;

  name: string;

  uuid: string;

  roleList: string[];

  rolesString: string;

  constructor(scope: RoleByScope, lang: string) {
    this.t = i18next.getFixedT(lang, "editor-users");
    this.name = scope?.name || "";
    this.uuid = scope?.uuid || "";
    this.roleList = AdminRoleByScope.convertRolesFromBackend(scope?.roles);

    this.rolesString = this.generateRolesString();
  }

  static rolesFromBackend: Record<string, string> = {
    [OWNER]: "owner",
    [EDITOR_ADMIN]: "editorAdmin",
    [PROJECT_ADMIN]: "projectAdmin",
    [CONTENT_MANAGER_ADMIN]: "contentManagerAdmin",
    [CONTENT_MANAGER]: "contentManager",
    [EDITORIAL_MANAGER]: "editorialManager",
    [BUSINESS_MANAGER]: "businessManager",
    [ANALYST_ADMIN]: "analystAdmin",
    [ANALYST]: "analyst",
    [MODERATOR_ADMIN]: "moderatorAdmin",
    [FAST_ADMIN]: "fastAdmin",
    [FAST_ANALYST]: "fastAnalyst",
  };

  static rolesToBackend: Record<string, string> = {
    owner: OWNER,
    editorAdmin: EDITOR_ADMIN,
    projectAdmin: PROJECT_ADMIN,
    contentManagerAdmin: CONTENT_MANAGER_ADMIN,
    contentManager: CONTENT_MANAGER,
    editorialManager: EDITORIAL_MANAGER,
    businessManager: BUSINESS_MANAGER,
    analystAdmin: ANALYST_ADMIN,
    analyst: ANALYST,
    moderatorAdmin: MODERATOR_ADMIN,
    fastAdmin: FAST_ADMIN,
    fastAnalyst: FAST_ANALYST,
  };

  static basicRoles: string[] = [
    "projectAdmin",
    "editorialManager",
    "businessManager",
    "analystAdmin",
    "analyst",
    "contentManager",
    "moderatorAdmin",
  ];

  static organizationRoles: string[] = ["editorAdmin", "contentManagerAdmin"];

  static fastChannelRoles: string[] = ["fastAdmin", "fastAnalyst"];

  static adminRoles: string[] = ["owner", "editorAdmin"];

  translateRoles = (roles: string[]): string => {
    const translatedRoles = roles?.map((role) => this.t(role));
    return translatedRoles?.join(", ");
  };

  get roleCount(): number {
    return this.roleList.length;
  }

  /**
   * Some roles are not compatible with each other
   * This ensures that the selected roles never conflict
   * @param currentRoles - previous list of roles
   * @param newRoles - new list of roles
   * @returns enforced updated list of roles
   */
  static rolesUpdate = (
    currentRoles: string[],
    newRoles: string[],
  ): Set<string> => {
    const currentRolesSet = new Set(currentRoles);
    const newRolesSet = new Set(newRoles);

    const rolePriorities = ["projectAdmin", "fastAdmin", "editorAdmin"];

    if (rolePriorities.some((role) => currentRolesSet.has(role))) {
      return new Set();
    }
    const newRole = rolePriorities.find((role) => newRolesSet.has(role));
    if (newRole) {
      return new Set([newRole]);
    }

    if (!currentRolesSet.has("analyst") && newRolesSet.has("analyst")) {
      return new Set([...newRoles, "contentManager"]);
    }

    return new Set(newRoles);
  };

  /**
   * Returns the roles for the user
   * @returns list of roles, comma separated
   */
  generateRolesString(): string {
    return this.translateRoles(this.roleList);
  }

  static convertRolesFromBackend = (roles: string[] | undefined): string[] => {
    if (!roles) {
      return [];
    }
    return roles.map((role) => AdminRoleByScope.rolesFromBackend[role] ?? role);
  };

  static convertRolesToBackend(roles: string[] | undefined): string[] {
    if (!roles) {
      return [];
    }
    return roles.map((role) => AdminRoleByScope.rolesToBackend[role] ?? "");
  }
}

export default AdminRoleByScope;
